/**
 * The lock-on marker (ui/lockOn.ts): while a foe is locked, two turning brackets at its feet and four corner marks round
 * its body, drawn over the roofs, so the locked one is never lost.
 */
import { duelMatchOf } from '@nine/shared';
import { globeBend } from '../globe';
import { ellipse } from '../drawKit';
import { registerRenderPass } from './registry';

/** What a fighter held in a locked form is doing (from the rules: systems/duelLock.ts poseOf), by entity id: drawn by the renderer. */
export interface FormPose {
  role: 'caster' | 'target';
  kind: string;
  k: number;
  u: number;
  ax: number;
  ay: number;
}
export const lockMark = { id: -1, t: 0, form: null as [number, number, number] | null, pose: new Map<number, FormPose>() };
if (import.meta.env.DEV) Object.assign(window, { __lockMark: lockMark });

registerRenderPass({
  id: 'lockFeet',
  layer: 'ground',
  order: 228,
  draw({ g, world: w, iso, ox, oy }) {
    const e = lockMark.id >= 0 ? w.get(lockMark.id) : undefined;
    if (!e || e.dead) return;
    if (duelMatchOf(w)?.current) return; // a bout: render/passes/duelFoeMark.ts draws the stepped pixel ring
    const [x, y] = iso.proj(e.x, e.y);
    const cx = x + ox;
    const cy = y + oy;
    const r = 1.0 * 22.6 * Math.max(1, e.scale);
    g.save();
    g.lineWidth = 2;
    g.strokeStyle = 'rgba(255,236,170,.95)';
    // two arcs of a ring, turning
    for (const k of [0, Math.PI]) {
      const a = lockMark.t * 2.2 + k;
      g.beginPath();
      g.ellipse(cx, cy, r, r / 2, 0, a, a + 1.5);
      g.stroke();
    }
    g.strokeStyle = 'rgba(40,24,12,.6)';
    g.lineWidth = 1;
    ellipse(g, cx, cy, r + 2, r / 2 + 1);
    g.stroke();
    g.restore();
  },
});

registerRenderPass({
  id: 'lockBody',
  layer: 'labels',
  order: 105,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    const e = lockMark.id >= 0 ? w.get(lockMark.id) : undefined;
    if (!e || e.dead) return;
    const [sx, sy] = iso.proj(e.x, e.y);
    const x = (sx + ox) * PX;
    const y = globeBend(sx + ox, sy + oy - 22 * e.scale)[1] * PX;
    const hw = 20 + Math.sin(lockMark.t * 6) * 2;
    const hh = 30 + Math.sin(lockMark.t * 6) * 2;
    const c = 8;
    mg.save();
    mg.lineJoin = 'round';
    for (const [w2, col] of [
      [5, 'rgba(12,9,14,.9)'],
      [2.5, '#ffe9a8'],
    ] as const) {
      mg.lineWidth = w2;
      mg.strokeStyle = col;
      mg.beginPath();
      for (const [sxn, syn] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const) {
        const px = x + sxn * hw;
        const py = y + syn * hh;
        mg.moveTo(px - sxn * c, py);
        mg.lineTo(px, py);
        mg.lineTo(px, py - syn * c);
      }
      mg.stroke();
    }
    mg.restore();
  },
});

// A locked form (a flurry or heavy lock move that connected): a golden tether between the two fighters while both are held.
registerRenderPass({
  id: 'lockForm',
  layer: 'labels',
  order: 106,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    const f = lockMark.form;
    if (!f) return;
    const a = w.get(f[0]);
    const b = w.get(f[1]);
    if (!a || !b) return;
    const pt = (e: typeof a) => {
      const [sx, sy] = iso.proj(e.x, e.y);
      return [(sx + ox) * PX, globeBend(sx + ox, sy + oy - 16 * e.scale)[1] * PX] as const;
    };
    const [x1, y1] = pt(a);
    const [x2, y2] = pt(b);
    mg.save();
    mg.lineCap = 'round';
    for (const [lw, col] of [
      [7, 'rgba(12,9,14,.55)'],
      [3, `rgba(255,222,140,${0.6 + 0.35 * Math.sin(lockMark.t * 18)})`],
    ] as const) {
      mg.lineWidth = lw;
      mg.strokeStyle = col;
      mg.beginPath();
      mg.moveTo(x1, y1);
      mg.lineTo(x2, y2);
      mg.stroke();
    }
    mg.restore();
  },
});
